/**
 * draftSanitizer — strip agent-internal machinery from a section's
 * draft_content before it lands in the writer's Word document.
 *
 * Why this exists
 * ---------------
 * The agent emits each module's draft with a fenced subtask-manifest
 * JSON block as the first non-whitespace content (per agent_rules.yaml
 * + flags.yaml in csr_sim). The review-agent sidecar parses that
 * block to populate the Coverage tab — it's load-bearing for the
 * audit pipeline.
 *
 * BUT it has no place in the writer-facing prose. When section_ready
 * fires, ``draft_content`` carries both the manifest AND the prose;
 * passing it raw to insertSectionContent puts the JSON into the doc.
 *
 * This module strips the agent's internal markers without touching
 * the prose:
 *   - Removes ```subtask-manifest ... ``` fenced blocks.
 *   - Removes [SUBTASK-BEGIN: id | title] / [SUBTASK-END] flag lines
 *     (per flags.yaml — they bracket subtask bodies inside the prose
 *     for the parser to find, but the writer doesn't read them).
 *
 * Where this should live long-term
 * --------------------------------
 * The strip is a Word-write concern shared by ANY UI that inserts
 * draft_content into a doc — classic UI is also affected in section
 * mode. Promoting this helper to ``services/wordWriter.ts`` so both
 * UIs use it is the right cleanup, deferred to a separate commit
 * because wordWriter.ts is hot-path-adjacent and the user's earlier
 * direction was to keep Variant C work isolated. Tracked in spec 15.
 *
 * SRP: pure function. No state, no I/O. Same input → same output.
 */

// Matches the agent's subtask-manifest fence, opening + closing,
// across newlines. ``[\s\S]*?`` is a non-greedy "any char including
// newline" — pre-ES2018 regex compat (don't need /s flag this way).
const SUBTASK_MANIFEST_RE = /```subtask-manifest[\s\S]*?```\s*/g;

// OFF-grammar manifest leak the canonical fence above misses: a degraded
// writer model (qwen on Groq skipping the forced tool call) prints the
// manifest as a ```json (or bare) object at the top of its text — the
// observed ``{"version":"1.0","subtasks":[]}`` under "9.3.2". Identified by a
// JSON object carrying a ``subtasks`` key. Mirror of
// strip_leaked_subtask_manifest_json in src/csr_sim/draft_sanitizer.py.
const SUBTASK_MANIFEST_JSON_FENCE_RE =
  /```[A-Za-z0-9_-]*[ \t]*\n[ \t]*\{[\s\S]*?"subtasks"[\s\S]*?\}[ \t]*\n?```\s*/g;
// A leading bare (unfenced) manifest object, no nested braces (covers the
// empty/flat manifest form). Anchored to the start so body JSON is untouched.
const SUBTASK_MANIFEST_JSON_BARE_RE = /^\s*\{[^{}]*"subtasks"[^{}]*\}[ \t]*\n?/;

// Matches the [SUBTASK-BEGIN: ...] / [SUBTASK-END: ...] /
// [SUBTASK-MANIFEST-END] flag lines as whole lines (with surrounding
// whitespace including line break). All three forms can carry
// trailing content before the closing bracket (e.g.
// "[SUBTASK-END: ST-FRM-607-01]") — the ``[^\]]*`` segment absorbs
// it. ``[SUBTASK-MANIFEST-END]`` first observed on the CMC run
// 2026-04-28 (added to capture the trailing marker the agent emits
// at end-of-draft); BEGIN/END history goes back to feat/variant-c-
// loom-spine-ui (2026-04-21).
const SUBTASK_FLAG_RE = /^\s*\[SUBTASK-(?:BEGIN|END|MANIFEST-END)[^\]]*\]\s*$\n?/gm;

// Reasoning blocks a model might leak (<think>…</think> / <thinking>…</thinking>).
// Anchored to the exact tag names — no attributes, no spaces — so real prose
// like "<thinking about X>" is never touched. Well-formed spans strip content
// and all; any orphan open/close tag left over strips tag-only (conservative:
// an unclosed <think> prefix keeps its body rather than risk eating prose).
// Mirror of _strip_think_blocks in server/routes/selection_edit.py.
const THINK_SPAN_RE = /<think(?:ing)?>[\s\S]*?<\/think(?:ing)?>\s*/gi;
const THINK_ORPHAN_TAG_RE = /<\/?think(?:ing)?>\s*/gi;

// Special-token machinery (writer-model drift, NOT part of any protocol).
// Non-Anthropic writer models (Groq qwen/qwen3-32b, gpt-oss) natively use
// ``<|...|>`` special tokens and occasionally invent their own planning
// manifests wrapped in that syntax, e.g. ``<|SUBTASK-MANIFEST-START|> {json}
// <|SUBTASK-MANIFEST-END|>``. ``<|...|>`` never appears in real prose, so we
// strip it delimiter-agnostically. Mirror of _SPECIAL_TOKEN_SPAN_RE /
// _SPECIAL_TOKEN_RE in review_agent/subtask_parser.py.
// Paired span first (payload and all), then any standalone/orphan token.
const SPECIAL_TOKEN_SPAN_RE =
  /<\|[^|>\n]*?(?:START|BEGIN)\|>[\s\S]*?<\|[^|>\n]*?END\|>[ \t]*\n?/gi;
// Trailing whitespace/newline consumed (clean line removal) but NOT leading —
// an inline ``word <|tok|> word`` collapses to ``word word``, not ``wordword``.
const SPECIAL_TOKEN_RE = /<\|[^|>\n]*\|>[ \t]*\n?/g;

// A transaction-id token: ``txn-<hex>`` (ledger-local) or a full UUID
// (Asthra). Mirrors _TXN_TOKEN in src/csr_sim/draft_sanitizer.py.
const TXN_TOKEN = "(?:txn-[0-9a-f]{6,}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})";

// Inline transaction-id citation the drafting model appends to sentences,
// e.g. ``[txn-1a2b3c4d]`` or ``[Transaction: <uuid>]``. A leading run of
// spaces/tabs is consumed so "… significant [txn-…]." → "… significant.".
// Real markers ([NOT FOUND …], [REVIEWER: …], [CANNOT RESOLVE: …]) contain
// no transaction token and are preserved.
const INLINE_TXN_RE = new RegExp(
  `[ \\t]*\\[\\s*(?:(?:transaction|txn|source|ref|provenance)s?\\s*[:=]\\s*)?${TXN_TOKEN}(?:\\s*[,;]\\s*${TXN_TOKEN})*\\s*\\]`,
  "gi",
);

// A line that is nothing but a markdown code fence (``` or ```lang). Run
// AFTER the subtask-manifest fence is removed so we only catch stray fences.
const FENCE_LINE_RE = /^[ \t]*`{3,}[A-Za-z0-9_-]*[ \t]*$/gm;

// An internal cascade marker the drafting model echoes from the retrieved-
// context headings, e.g. ``【primary_endpoint_result::followup::L2】``: a
// ``::``-joined identifier path carrying at least one cascade segment
// (``::followup`` or ``::L<digits>``), optionally wrapped in CJK corner
// brackets 【 】 or square brackets. The required cascade-segment lookahead
// keeps acknowledged-gap markers ([MISSING INFO …], [CANNOT RESOLVE …]) safe —
// they contain no ``::``. The bracketed branch eats whitespace inside the
// brackets; the bare branch must NOT eat trailing whitespace/newlines (that
// would weld neighbouring words/lines together). A leading run of spaces/tabs
// is shared. Mirrors _CASCADE_MARKER_RE in draft_sanitizer.py.
const CASCADE_PATH =
	"(?=[A-Za-z0-9_:]*::(?:followup|L\\d+))[A-Za-z0-9_]+(?:::[A-Za-z0-9_]+)*";
const CASCADE_MARKER_RE = new RegExp(
	`[ \\t]*(?:[【[]\\s*${CASCADE_PATH}\\s*[】\\]]|${CASCADE_PATH})`,
	"g",
);

// A heading line left empty after its cascade label was stripped (e.g. "### ").
const EMPTY_HEADING_RE = /^[ \t]*#{1,6}[ \t]*$\n?/gm;

// gpt-oss on Groq intermittently double-escapes its submit_draft JSON args,
// so the parsed draft_text carries the literal two-character sequences \n /
// \r\n / \t (backslash + letter) instead of real breaks — the whole section
// then renders as one paragraph littered with visible \n. Mirror of
// _ESCAPED_WS_RE in src/csr_sim/draft_sanitizer.py.
const ESCAPED_WS_RE = /\\r\\n|\\n|\\t/g;
const ESCAPED_WS_MAP: Record<string, string> = {
  "\\r\\n": "\n",
  "\\n": "\n",
  "\\t": "\t",
};

// A literal \n escape immediately before a list marker is never legitimate
// prose — rewritten unconditionally (the majority guard below misses mixed
// drafts where only the glued list is escaped). Mirror of
// _ESCAPED_LIST_BREAK_RE in src/csr_sim/draft_sanitizer.py.
const ESCAPED_LIST_BREAK_RE = /(?:\\r)?\\n(?=\d{1,2}\.\s|[-*]\s)/g;

// Backslash-dropped variant of the same corruption (qwen3-32b): a bare "n"
// glues list items — "…lactation.n4. Has received…". Healed only when >=2
// seams form a strictly increasing number run. Mirror of
// _BARE_N_LIST_SEAM_RE / heal_bare_newline_list_runs in draft_sanitizer.py.
const BARE_N_LIST_SEAM_RE = /(?<=[.)\]:])n(\d{1,2})(?=\.\s)/g;

export function healBareNewlineListRuns(text: string): string {
  if (!text) return text;
  const matches = [...text.matchAll(BARE_N_LIST_SEAM_RE)];
  if (matches.length < 2) return text;

  // Group consecutive matches into maximal strictly-increasing number runs;
  // heal every run of length >= 2 (two separately-glued lists both heal).
  const heal = new Set<number>();
  let run: number[] = [0];
  for (let i = 1; i < matches.length; i++) {
    if (parseInt(matches[i][1], 10) > parseInt(matches[run[run.length - 1]][1], 10)) {
      run.push(i);
    } else {
      if (run.length >= 2) run.forEach((k) => heal.add(k));
      run = [i];
    }
  }
  if (run.length >= 2) run.forEach((k) => heal.add(k));
  if (heal.size === 0) return text;

  let out = "";
  let last = 0;
  matches.forEach((m, i) => {
    if (!heal.has(i)) return;
    const start = m.index ?? 0;
    out += text.slice(last, start) + "\n" + m[1];
    last = start + m[0].length;
  });
  out += text.slice(last);
  return out;
}

// Count occurrences of a fixed substring (no regex-special handling needed
// for the literal "\n" / "\n" forms below).
function countOccurrences(haystack: string, needle: string): number {
  let n = 0;
  let i = haystack.indexOf(needle);
  while (i !== -1) {
    n += 1;
    i = haystack.indexOf(needle, i + needle.length);
  }
  return n;
}

// Rewrite literal \n / \r\n / \t escapes to real whitespace, but only when the
// draft actually relies on escaped breaks for its line structure (at least as
// many literal \n as real ones). A clean draft with a lone stray \n is left
// untouched. Mirror of normalize_escaped_newlines in draft_sanitizer.py.
export function normalizeEscapedNewlines(text: string): string {
  if (!text) return text;
  // List-shaped escapes first — always corruption, exempt from the guard.
  text = text.replace(ESCAPED_LIST_BREAK_RE, "\n");
  const literal = countOccurrences(text, "\\n");
  if (literal > 0 && literal >= countOccurrences(text, "\n")) {
    return text.replace(ESCAPED_WS_RE, (m) => ESCAPED_WS_MAP[m]);
  }
  return text;
}

// [CANNOT RESOLVE: …] → [MISSING INFO: …]. The narrative drafting prompt
// in agent_v3 historically asked for the former; the registry-driven marker
// in flags.yaml is [MISSING INFO:. Mirror of _CANNOT_RESOLVE_RE in
// src/csr_sim/draft_sanitizer.py.
const CANNOT_RESOLVE_RE = /\[\s*CANNOT\s+RESOLVE\s*[:\-–]\s*([^\]]+)\]/gi;

// [IB CONTENT NEEDED: …] → [MISSING INFO: …]. The `ib_content_needed` marker was
// retired from flags.yaml (the IB-sourced CSR Introduction reached for it instead
// of the canonical gap marker); any residual emission is collapsed to the registry
// form. Mirror of _IB_CONTENT_NEEDED_RE in src/csr_sim/draft_sanitizer.py.
const IB_CONTENT_NEEDED_RE = /\[\s*IB\s+CONTENT\s+NEEDED\s*[:\-–]\s*([^\]]+)\]/gi;

// Prefixes of registered flags.yaml markers the renumberer must NOT treat as
// citations. Mirror of the flags.yaml prefix list (longest first so MISSING
// TABLE is checked before MISSING INFO, etc.).
const REGISTERED_MARKER_PREFIXES = [
  "[NOT FOUND - NOT IN SOURCE]",
  "[NOT FOUND - RETRIEVAL FAILURE]",
  "PLACEHOLDER - REFER TO SOURCE DOCUMENTS",
  "[IB CONTENT NEEDED:", // retired marker; back-compat, normalized before this runs
  "[SUBTASK-MANIFEST-END]",
  "[SUBTASK-BEGIN:",
  "[SUBTASK-END:",
  "[MISSING TABLE",
  "[MISSING INFO:",
  "[CANNOT RESOLVE:", // back-compat; normalized before this runs
  "[REGULATOR FLAG:",
  "[LANGUAGE:",
  "[REVIEWER:",
  "[NOT FOUND",
  "[PENDING",
  "[ERROR:",
  "[FILL:",
];

// Bracket candidate for the renumberer: no nested brackets, no newlines,
// word-boundary lookarounds so arr[0]-style indexing is never touched.
const CITATION_BRACKET_RE = /(?<!\w)\[\s*([^\[\]\n]+?)\s*\](?!\w)/g;

/** Bracket-marker classifier the citation-marker writer uses to decide which
 *  ``[…]`` tokens deserve to be wrapped in a clickable content control.
 *  Returns the matched-prefix tag (e.g. ``"[MISSING INFO:"``) if the bracket
 *  is a registered flags.yaml marker — otherwise ``null``. Callers must NOT
 *  wrap registered markers (they're not citations). */
export function matchFlagMarker(bracket: string): string | null {
  for (const prefix of REGISTERED_MARKER_PREFIXES) {
    if (bracket.startsWith(prefix)) return prefix;
  }
  return null;
}

/** Citation-shape classifier the citation-marker writer uses. Returns the
 *  parsed canonical key (e.g. ``"1"``, ``"Smith2024"``, ``"1,2"``) when the
 *  bracket is a renderable citation; ``null`` otherwise. Matches the shapes
 *  ``renumberCitations`` already understands. */
export function classifyCitationBracket(body: string): string | null {
  const trimmed = body.trim();
  const rangeMatch = trimmed.match(CITATION_RANGE_RE);
  if (rangeMatch) return `${rangeMatch[1]}-${rangeMatch[2]}`;
  if (CITATION_NUM_LIST_RE.test(trimmed))
    return trimmed.split(/[,;]/).map((s) => s.trim()).join(",");
  if (CITATION_AUTHOR_YEAR_RE.test(trimmed)) return trimmed;
  return null;
}

/** Word-boundary citation-bracket regex re-exported so the Word writer can
 *  walk inserted text the same way the renumberer does (no double-wrapping
 *  ``arr[0]`` style indexing, no nested brackets, etc.). */
export const CITATION_BRACKET_PATTERN = CITATION_BRACKET_RE;

// Citation body shapes. Anything failing all three is left alone.
const CITATION_RANGE_RE = /^(\d+)\s*[-–]\s*(\d+)$/;
const CITATION_NUM_LIST_RE = /^\d+(\s*[,;]\s*\d+)*$/;
const CITATION_AUTHOR_YEAR_RE = /^[A-Z][A-Za-z]+\d{4}[a-z]?$/;

// Markdown level-2 section heading — ``## N``, ``## N.N``, ``## N.N.N``…
// The add-in's ``splitDraftBySections`` slices a module draft along the same
// boundary; per-section citation renumbering anchors here so the writer-facing
// numbering restarts at [1] at every section heading.  Mirror of
// _SECTION_HEADING_RE in src/csr_sim/draft_sanitizer.py.
const SECTION_HEADING_RE = /^##[ \t]+\d+(?:\.\d+)*\b.*$/gm;

// Heading-with-parts grammar for the demotion pass: captures lead whitespace,
// the section number, and the trailing title. Mirror of
// _NUMBERED_HEADING_PARTS_RE in src/csr_sim/draft_sanitizer.py.
const NUMBERED_HEADING_PARTS_RE = /^([ \t]*)##[ \t]+(\d+(?:\.\d+)*)[ \t.\-–:]*(.*?)[ \t]*$/;

/**
 * Rewrite [CANNOT RESOLVE: …] → [MISSING INFO: …] so the saved/displayed
 * draft uses the single registry marker regardless of which form the agent
 * emitted. Mirror of normalize_cannot_resolve_to_missing_info() in the
 * Python sanitizer. Idempotent.
 */
export function normalizeCannotResolveToMissingInfo(text: string): string {
  if (!text || text.indexOf("[") === -1) return text;
  return text.replace(CANNOT_RESOLVE_RE, (_m, body) => `[MISSING INFO: ${body.trim()}]`);
}

/**
 * Rewrite [IB CONTENT NEEDED: …] → [MISSING INFO: …] so the saved/displayed
 * draft uses the single registry marker. The `ib_content_needed` marker was
 * retired from flags.yaml; the IB-sourced CSR Introduction reached for it in
 * place of the canonical gap marker. Mirror of
 * normalize_ib_content_needed_to_missing_info() in the Python sanitizer.
 * Idempotent.
 */
export function normalizeIbContentNeededToMissingInfo(text: string): string {
  if (!text || text.indexOf("[") === -1) return text;
  return text.replace(IB_CONTENT_NEEDED_RE, (_m, body) => `[MISSING INFO: ${body.trim()}]`);
}

// Regulator-flag strip regexes — mirror of _MARKER_RE / _TRAILING_BLOCK_RE in
// src/csr_sim/regulatory/auditor.py. The inline marker is optionally preceded
// by a single space; the trailing block is the "**Regulatory review
// recommendations**" header plus its markers to EOF.
const REGULATOR_MARKER_RE = / ?\[REGULATOR FLAG:[^\]]*\]/g;
const REGULATOR_TRAILING_BLOCK_RE =
  /\n*\*\*Regulatory review recommendations\*\*\n+(?:\[REGULATOR FLAG:[^\]]*\]\n*)*\s*$/;

/**
 * Remove every ``[REGULATOR FLAG: …]`` marker and the trailing "Regulatory
 * review recommendations" block from a draft. Mirror of
 * ``_strip_regulator_markers`` in the Python auditor: block first, then inline
 * markers, then trailing-whitespace trim. Idempotent; leaves data-gap /
 * language / reviewer markers untouched.
 *
 * Used to project a section into the live doc WITHOUT its regulatory flags
 * (the up-front "clean slate" before a re-audit) — the server draft still
 * carries the old flags until the audit's own strip-and-reinsert runs.
 */
export function stripRegulatorMarkers(text: string): string {
  if (!text || text.indexOf("[REGULATOR FLAG:") === -1) return text;
  return text.replace(REGULATOR_TRAILING_BLOCK_RE, "").replace(REGULATOR_MARKER_RE, "").replace(/\s+$/, "");
}

// Underscore emphasis (__bold__ / _italic_) — valid CommonMark, but neither
// renderer understands it, so it would leak literal underscores into Word.
// Word-boundary guards mirror CommonMark's intraword rule so snake_case /
// file_name / 3_5 stay untouched; [^_]+? content stops a span crossing a stray
// underscore. Mirror of _UNDERSCORE_BOLD_RE / _UNDERSCORE_ITALIC_RE in
// src/csr_sim/draft_sanitizer.py.
const UNDERSCORE_BOLD_RE = /(?<![\w*])__(?=\S)([^_]+?)(?<=\S)__(?![\w])/g;
const UNDERSCORE_ITALIC_RE = /(?<![\w*])_(?=\S)([^_]+?)(?<=\S)_(?![\w])/g;

/**
 * Rewrite __bold__ → **bold** and _italic_ → *italic* so the asterisk-only
 * renderers don't leak literal underscores. Bold runs first. Mirror of
 * normalize_underscore_emphasis() in the Python sanitizer. Idempotent.
 */
export function normalizeUnderscoreEmphasis(text: string): string {
  if (!text || text.indexOf("_") === -1) return text;
  return text.replace(UNDERSCORE_BOLD_RE, "**$1**").replace(UNDERSCORE_ITALIC_RE, "*$1*");
}

// Unicode sub/superscript characters → ASCII, for normalizing LLM output that
// emits CO₂ / ×10⁹ (often copied from retrieved source text) into the Pandoc
// marker form (~2~ / ^9^) both renderers understand. Only characters whose
// ASCII image fits the marker grammar are mapped (so ₌/⁼ are deliberately
// absent); a run longer than 15 chars is left as-is rather than emit a marker
// the renderers would refuse. Mirror of _UNICODE_SUB_MAP / _UNICODE_SUP_MAP
// in src/csr_sim/draft_sanitizer.py — the grammar SSOT; must stay in lockstep.
const UNICODE_SUB_MAP: Record<string, string> = {
  "₀": "0", "₁": "1", "₂": "2", "₃": "3", "₄": "4",
  "₅": "5", "₆": "6", "₇": "7", "₈": "8", "₉": "9",
  "₊": "+", "₋": "-", "₍": "(", "₎": ")",
  "ₐ": "a", "ₑ": "e", "ₒ": "o", "ₓ": "x", "ₕ": "h", "ₖ": "k",
  "ₗ": "l", "ₘ": "m", "ₙ": "n", "ₚ": "p", "ₛ": "s", "ₜ": "t",
};
const UNICODE_SUP_MAP: Record<string, string> = {
  "⁰": "0", "¹": "1", "²": "2", "³": "3", "⁴": "4",
  "⁵": "5", "⁶": "6", "⁷": "7", "⁸": "8", "⁹": "9",
  "⁺": "+", "⁻": "-", "⁽": "(", "⁾": ")", "ⁿ": "n", "ⁱ": "i",
};
const UNICODE_SUB_RUN_RE = new RegExp(`[${Object.keys(UNICODE_SUB_MAP).join("")}]+`, "g");
const UNICODE_SUP_RUN_RE = new RegExp(`[${Object.keys(UNICODE_SUP_MAP).join("")}]+`, "g");
const SUB_SUP_MAX_LEN = 15;

/**
 * Rewrite Unicode sub/superscript runs to marker form (``₂₃`` → ``~23~``,
 * ``⁹`` → ``^9^``). Consecutive characters merge into one marker; an
 * over-long run stays untouched (renders as the literal Unicode chars —
 * benign). Mirror of normalize_unicode_sub_sup() in the Python sanitizer.
 * Idempotent — the output contains no Unicode sub/sup characters.
 */
export function normalizeUnicodeSubSup(text: string): string {
  if (!text) return text;
  const convert = (map: Record<string, string>, openClose: string) => (run: string): string => {
    const ascii = Array.from(run)
      .map((ch) => map[ch])
      .join("");
    if (ascii.length > SUB_SUP_MAX_LEN) return run;
    return `${openClose}${ascii}${openClose}`;
  };
  return text
    .replace(UNICODE_SUB_RUN_RE, convert(UNICODE_SUB_MAP, "~"))
    .replace(UNICODE_SUP_RUN_RE, convert(UNICODE_SUP_MAP, "^"));
}

/**
 * Renumber bracketed citations sequentially in first-appearance order.
 * Numeric ([3], [3, 5], [2-4]) and author-year ([Smith2024]) brackets are
 * treated as citations; registered flag markers and unknown bracket shapes
 * are left untouched. Mirror of renumber_citations() in the Python sanitizer.
 */
export function renumberCitations(text: string): string {
  if (!text || text.indexOf("[") === -1) return text;

  const remap = new Map<string, number>();
  let counter = 0;
  const getId = (key: string): number => {
    let id = remap.get(key);
    if (id === undefined) {
      counter += 1;
      id = counter;
      remap.set(key, id);
    }
    return id;
  };

  return text.replace(CITATION_BRACKET_RE, (match, rawBody, offset: number) => {
    const body = (rawBody as string).trim();

    // Markdown reference-link at line start (``^[1]: url``) — never touch.
    const atLineStart = offset === 0 || text.charAt(offset - 1) === "\n";
    if (atLineStart && text.charAt(offset + match.length) === ":") return match;

    // Registered marker (flags.yaml) — skip. Longest-prefix wins (the array
    // is ordered so MISSING TABLE wins over MISSING INFO, etc.).
    for (const prefix of REGISTERED_MARKER_PREFIXES) {
      if (match.startsWith(prefix)) return match;
    }

    const rangeMatch = body.match(CITATION_RANGE_RE);
    if (rangeMatch) {
      const startN = parseInt(rangeMatch[1], 10);
      const endN = parseInt(rangeMatch[2], 10);
      if (startN > endN) return match;
      const newIds: number[] = [];
      for (let n = startN; n <= endN; n += 1) newIds.push(getId(String(n)));
      const isContiguous =
        newIds.length >= 2 &&
        newIds.every((v, i) => i === 0 || v === newIds[i - 1] + 1);
      return isContiguous
        ? `[${newIds[0]}-${newIds[newIds.length - 1]}]`
        : `[${newIds.join(", ")}]`;
    }

    if (CITATION_NUM_LIST_RE.test(body)) {
      const nums = body.split(/[,;]/).map((s) => parseInt(s.trim(), 10));
      const newIds = nums.map((n) => getId(String(n)));
      return `[${newIds.join(", ")}]`;
    }

    if (CITATION_AUTHOR_YEAR_RE.test(body)) {
      return `[${getId(body)}]`;
    }

    return match;
  });
}

/**
 * Renumber citations independently within each ``## N`` section block.
 *
 * A module draft may span multiple sub-section headings (``## 9.2``,
 * ``## 9.3``…); per the writer-facing contract, citation numbering restarts
 * at [1] at every level-2 heading so each sub-section reads standalone.
 *
 * Mirror of ``renumber_citations_per_section`` in
 * ``src/csr_sim/draft_sanitizer.py``. Idempotent: the server already
 * produces per-section [1..N] at submit_draft, so this pass is defense-
 * in-depth and should leave a well-formed draft unchanged.
 */
export function renumberCitationsPerSection(text: string): string {
  if (!text) return text;
  // Reset lastIndex — SECTION_HEADING_RE is /g and is reused across calls.
  SECTION_HEADING_RE.lastIndex = 0;
  const headings: number[] = [];
  let m: RegExpExecArray | null;
  while ((m = SECTION_HEADING_RE.exec(text)) !== null) {
    headings.push(m.index);
    // Guard against zero-width matches stalling the loop.
    if (m.index === SECTION_HEADING_RE.lastIndex) SECTION_HEADING_RE.lastIndex += 1;
  }
  if (headings.length === 0) return renumberCitations(text);

  const parts: string[] = [];
  // Preamble before the first heading (rare, but possible) — renumber as its
  // own block so any citations there start at [1] too.
  if (headings[0] > 0) parts.push(renumberCitations(text.slice(0, headings[0])));
  for (let i = 0; i < headings.length; i += 1) {
    const end = i + 1 < headings.length ? headings[i + 1] : text.length;
    parts.push(renumberCitations(text.slice(headings[i], end)));
  }
  return parts.join("");
}

/**
 * Demote rogue ``## N.x <Title>`` headings whose number isn't in
 * ``authoritativeIds`` to unnumbered ``**Title**`` bold subtitles.
 *
 * A numbered sub-section heading is authoritative when the current
 * module is the designated drafter for that skeleton row. Anything else
 * is a model invention that would create a duplicate entry in the
 * rendered document. Mirror of demote_unauthorized_numbered_headings()
 * in src/csr_sim/draft_sanitizer.py.
 *
 * ``authoritativeIds = null`` is the legacy contract: no demotion at
 * all. Defense-in-depth — the server sanitizer already runs this pass
 * at submit_draft when the orchestrator knows the spine state; this
 * client-side mirror catches drafts that came through paths where the
 * server didn't have the authoritative set.
 */
export function demoteUnauthorizedNumberedHeadings(
  text: string,
  authoritativeIds: Set<string> | null,
): string {
  if (!text || authoritativeIds === null || text.indexOf("##") === -1) return text;
  const out: string[] = [];
  for (const line of text.split("\n")) {
    const m = line.match(NUMBERED_HEADING_PARTS_RE);
    if (!m) {
      out.push(line);
      continue;
    }
    const num = m[2];
    if (authoritativeIds.has(num)) {
      out.push(line);
      continue;
    }
    const title = (m[3] || "").trim();
    if (!title) continue; // drop "## 5.1" with no title outright
    out.push(`${m[1]}**${title}**`);
  }
  return out.join("\n");
}

// ---------------------------------------------------------------------------
// Structural markup repair — mirror of the passes in draft_sanitizer.py
// ---------------------------------------------------------------------------

// Pipe-table row / separator. Mirror of _RE_TABLE_ROW / _RE_TABLE_SEP in
// draft_sanitizer.py (which itself mirrors word_writer.py).
const RE_TABLE_ROW = /^\|(.+?)\|?\s*$/;
const RE_TABLE_SEP = /^\|[\s\-:|]+\|?\s*$/;
// Paragraph boundary (blank line), captured so split() keeps separators and the
// text rejoins byte-for-byte. Mirror of _PARAGRAPH_SPLIT_RE.
const PARAGRAPH_SPLIT_RE = /(\n[ \t]*\n)/;
// A ** / *** emphasis delimiter run. Mirror of _BOLD_DELIM_RE.
const BOLD_DELIM_RE = /\*{2,3}/g;
// A markdown link / image span. Mirror of _LINK_SPAN_RE.
const LINK_SPAN_RE = /!?\[[^\]\n]*\]\([^)\n]*\)/g;

function tableCells(line: string): string[] {
  const m = RE_TABLE_ROW.exec(line.trim());
  if (!m) return [];
  return m[1].split("|").map((c) => c.trim());
}

function iterTableBlocks(lines: string[]): Array<[number, number]> {
  const blocks: Array<[number, number]> = [];
  let i = 0;
  const n = lines.length;
  while (i < n) {
    if (RE_TABLE_ROW.test(lines[i].trim())) {
      const start = i;
      while (i < n && RE_TABLE_ROW.test(lines[i].trim())) i += 1;
      if (i - start >= 2) blocks.push([start, i]);
    } else {
      i += 1;
    }
  }
  return blocks;
}

function formatRow(cells: string[]): string {
  return `| ${cells.join(" | ")} |`;
}

function formatSep(nCols: number): string {
  return `| ${Array(nCols).fill("---").join(" | ")} |`;
}

function normalizeCells(cells: string[], nCols: number): string[] {
  if (cells.length < nCols) return [...cells, ...Array(nCols - cells.length).fill("")];
  if (cells.length > nCols) {
    const keep = cells.slice(0, nCols);
    const overflow = cells.slice(nCols).filter((c) => c.trim());
    if (overflow.length > 0) {
      keep[keep.length - 1] = `${keep[keep.length - 1]} ${overflow.join(" ")}`.trim();
    }
    return keep;
  }
  return cells;
}

function repairTableBlock(block: string[]): string[] {
  let headerIdx = -1;
  for (let k = 0; k < block.length; k += 1) {
    if (!RE_TABLE_SEP.test(block[k].trim())) {
      headerIdx = k;
      break;
    }
  }
  if (headerIdx === -1) return block;
  const nCols = tableCells(block[headerIdx]).length;
  if (nCols < 1) return block;
  const hasSep = block.some((ln) => RE_TABLE_SEP.test(ln.trim()));
  const out: string[] = [];
  for (let k = 0; k < block.length; k += 1) {
    const line = block[k];
    if (RE_TABLE_SEP.test(line.trim())) out.push(formatSep(nCols));
    else out.push(formatRow(normalizeCells(tableCells(line), nCols)));
    if (k === headerIdx && !hasSep) out.push(formatSep(nCols));
  }
  return out;
}

/**
 * Normalize ragged pipe tables to the header row's column count. Pads short
 * rows, folds non-empty overflow into the last cell (never dropping content),
 * regenerates/inserts the separator. Mirror of repair_ragged_tables() in
 * draft_sanitizer.py. Pure and idempotent.
 */
export function repairRaggedTables(text: string): string {
  if (!text || text.indexOf("|") === -1) return text;
  const lines = text.split("\n");
  const blocks = iterTableBlocks(lines);
  if (blocks.length === 0) return text;
  const out: string[] = [];
  let prevEnd = 0;
  for (const [start, end] of blocks) {
    out.push(...lines.slice(prevEnd, start));
    out.push(...repairTableBlock(lines.slice(start, end)));
    prevEnd = end;
  }
  out.push(...lines.slice(prevEnd));
  return out.join("\n");
}

function closeBoldInParagraph(para: string): string {
  if (para.indexOf("`") !== -1) return para;
  // Count non-overlapping `**` delimiters — keeps this idempotent on adjacent
  // runs like `**a****b**` (even count = balanced; appending would loop forever).
  // Mirror of _close_bold_in_paragraph in draft_sanitizer.py.
  const count = countOccurrences(para, "**");
  if (count % 2 === 0) return para;
  const stripped = para.replace(/\s+$/, "");
  const trailing = para.slice(stripped.length);
  return `${stripped}**${trailing}`;
}

/**
 * Close an unterminated **bold** span at paragraph end. Skips paragraphs with
 * backticks. Mirror of close_unbalanced_bold_markers() in draft_sanitizer.py.
 */
export function closeUnbalancedBoldMarkers(text: string): string {
  if (!text || text.indexOf("**") === -1) return text;
  const parts = text.split(PARAGRAPH_SPLIT_RE);
  for (let i = 0; i < parts.length; i += 2) parts[i] = closeBoldInParagraph(parts[i]);
  return parts.join("");
}

function maskRecognizedBrackets(para: string): string {
  let masked = para.replace(LINK_SPAN_RE, (m) => " ".repeat(m.length));
  masked = masked.replace(CITATION_BRACKET_RE, (full: string, body: string) => {
    const b = body.trim();
    if (
      matchFlagMarker(full) ||
      CITATION_RANGE_RE.test(b) ||
      CITATION_NUM_LIST_RE.test(b) ||
      CITATION_AUTHOR_YEAR_RE.test(b)
    ) {
      return " ".repeat(full.length);
    }
    return full;
  });
  return masked;
}

function balanceBracketsInParagraph(para: string): string {
  const masked = maskRecognizedBrackets(para);
  const opens = (masked.match(/\[/g) || []).length;
  const closes = (masked.match(/\]/g) || []).length;
  if (opens <= closes) return para; // surplus/orphaned closers left untouched
  const stripped = para.replace(/\s+$/, "");
  const trailing = para.slice(stripped.length);
  return `${stripped}${"]".repeat(opens - closes)}${trailing}`;
}

/**
 * Close an unterminated `[` (truncation artifact) at paragraph end, after
 * masking recognized flag markers / citations / links. Surplus closers are
 * left alone. Mirror of balance_orphaned_brackets() in draft_sanitizer.py.
 */
export function balanceOrphanedBrackets(text: string): string {
  if (!text || text.indexOf("[") === -1) return text;
  const parts = text.split(PARAGRAPH_SPLIT_RE);
  for (let i = 0; i < parts.length; i += 2) parts[i] = balanceBracketsInParagraph(parts[i]);
  return parts.join("");
}

/**
 * Strip agent-internal machinery from a draft. Returns the cleaned
 * prose. Idempotent — calling twice has the same effect as once.
 *
 * Empty / undefined input returns "" so callers can pass directly
 * from defensive payload destructuring.
 *
 * ``authoritativeIds`` (optional) is the set of section ids the module
 * is the authoritative drafter for. When supplied, any ``## N.x`` whose
 * number isn't in the set is demoted to an unnumbered ``**Title**``.
 * When ``null``/omitted no demotion runs (legacy callers).
 */
export function sanitizeDraftForWord(
  content: string | undefined | null,
  authoritativeIds: Set<string> | null = null,
): string {
  if (!content) return "";
  // Un-escape literal \n / \t the drafting model double-escapes into its JSON
  // args FIRST, so every line-oriented pass below (fence/heading/citation)
  // sees properly-lined text instead of one giant line. The \n{3,} collapse
  // further down then squashes the tail of any degenerate repetition run.
  let cleaned = normalizeEscapedNewlines(content);
  // Repair the backslash-dropped variant of the same corruption (bare "n"
  // gluing list items) before any line-oriented pass.
  cleaned = healBareNewlineListRuns(cleaned);
  // Collapse the legacy [CANNOT RESOLVE: …] form to the registry [MISSING
  // INFO: …] form so the renumber pass below sees only the canonical marker
  // and the prose matches the single source of truth (flags.yaml).
  cleaned = normalizeCannotResolveToMissingInfo(cleaned);
  cleaned = normalizeIbContentNeededToMissingInfo(cleaned);
  // Normalize __bold__/_italic_ to the asterisk form the renderers understand,
  // before the fence/heading/citation passes below.
  cleaned = normalizeUnderscoreEmphasis(cleaned);
  // Rewrite Unicode sub/superscript characters (CO₂, ×10⁹) to the canonical
  // ~sub~/^sup^ marker form so both renderers see one representation.
  cleaned = normalizeUnicodeSubSup(cleaned);
  // Strip leaked reasoning blocks before anything line-oriented — a
  // <think> span can contain markers/fences that would otherwise be
  // half-processed by the passes below.
  cleaned = cleaned.replace(THINK_SPAN_RE, "");
  cleaned = cleaned.replace(THINK_ORPHAN_TAG_RE, "");
  // Strip leaked ``<|...|>`` special-token machinery FIRST (as whole blocks,
  // including any JSON payload) before the line-oriented marker passes below.
  cleaned = cleaned.replace(SPECIAL_TOKEN_SPAN_RE, "");
  cleaned = cleaned.replace(SPECIAL_TOKEN_RE, "");
  cleaned = cleaned.replace(SUBTASK_MANIFEST_RE, "");
  // Off-grammar manifest JSON (```json {"subtasks": …} / bare object) the
  // canonical fence above misses — remove before the stray-fence pass, which
  // would drop the fence lines but leave the JSON body behind.
  cleaned = cleaned.replace(SUBTASK_MANIFEST_JSON_FENCE_RE, "");
  cleaned = cleaned.replace(SUBTASK_MANIFEST_JSON_BARE_RE, "");
  cleaned = cleaned.replace(SUBTASK_FLAG_RE, "");
  // Drop stray code fences the model wrapped prose in (the subtask-manifest
  // fence is already gone above) and inline transaction-id citations it
  // appended to sentences. Defense-in-depth: the server already strips
  // transaction IDs at submit_draft, but gpt-oss-120b is unreliable.
  cleaned = cleaned.replace(FENCE_LINE_RE, "");
  cleaned = cleaned.replace(INLINE_TXN_RE, "");
  // Internal cascade markers (e.g. 【…::followup::L2】) the model echoes from
  // the retrieved-context headings. Acknowledged-gap markers are preserved.
  cleaned = cleaned.replace(CASCADE_MARKER_RE, "");
  cleaned = cleaned.replace(EMPTY_HEADING_RE, "");
  cleaned = cleaned.replace(/[ \t]{2,}/g, " ").replace(/[ \t]+([.,;:)])/g, "$1");
  // Collapse runs of 3+ blank lines that appear after stripping the
  // surrounding markers — they'd otherwise leave awkward gaps.
  cleaned = cleaned.replace(/\n{3,}/g, "\n\n");
  // Structural repairs (mirror of draft_sanitizer.py): normalize ragged tables
  // to the header's column count, then close a truncated **bold** span. Both
  // run before heading demotion / citation renumbering.
  cleaned = repairRaggedTables(cleaned);
  cleaned = closeUnbalancedBoldMarkers(cleaned);
  // Demote any rogue ``## N.x`` heading whose number isn't authoritative
  // for this draft to an unnumbered bold subtitle, so a leaked subsection
  // heading doesn't duplicate the child run's heading in the rendered doc.
  cleaned = demoteUnauthorizedNumberedHeadings(cleaned, authoritativeIds);
  // Sequential renumbering — last, so it sees the cleaned-up prose without
  // transaction-id brackets or cascade tokens that would skew the counter.
  // Per-section: numbering restarts at [1] at every ``## N`` heading so each
  // sub-section reads standalone in the rendered Word doc.
  cleaned = renumberCitationsPerSection(cleaned);
  // Close a truncated `[` at paragraph end LAST — only touches brackets the
  // flag/citation passes already declined. Mirror of balance_orphaned_brackets.
  cleaned = balanceOrphanedBrackets(cleaned);
  return cleaned.trim();
}
